import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import AdminCatchphraseQueue from "@/components/AdminCatchphraseQueue";
import AdminCatchphraseTrends from "@/components/AdminCatchphraseTrends";
import AdminTestUserPruner from "@/components/AdminTestUserPruner";
import AdminFeedbackList from "@/components/AdminFeedbackList";
import { useAuth } from "@/context/AuthContext";
import { Shield, Wrench, Palette, Coins, Car as CarIcon, Search, Trash2, Mail, Ban, ShieldCheck, Award, MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [query, setQuery] = useState("");

  const loadUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/users`, { withCredentials: true });
      setUsers(res.data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const loadDetail = useCallback(async (userId) => {
    setSelectedId(userId);
    setDetail(null);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/users/${userId}`, { withCredentials: true });
      setDetail(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load user detail");
    }
  }, []);

  // Permission gate — admin only.
  if (user && !user.is_admin) {
    return (
      <AppLayout>
        <div className="p-12 text-white" data-testid="admin-forbidden">
          <Shield className="w-10 h-10 text-[#FF3B30] mb-4" />
          <h1 className="font-heading text-4xl">Access denied</h1>
          <p className="text-white/60 mt-2">You do not have admin privileges.</p>
        </div>
      </AppLayout>
    );
  }

  const filteredUsers = users.filter((u) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      u.email?.toLowerCase().includes(q) ||
      u.name?.toLowerCase().includes(q) ||
      u.user_id?.toLowerCase().includes(q)
    );
  });

  return (
    <AppLayout>
      <div className="p-6 lg:p-10 text-white" data-testid="admin-page">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-6 h-6 text-[#FFCC00]" />
          <div>
            <div className="label-kicker">Operator Console</div>
            <h1 className="font-heading text-4xl lg:text-5xl uppercase tracking-tighter">Admin Garage</h1>
          </div>
        </div>

        {/* Catchphrase trending bad-pattern alerts (above the queue so
            admins triage hot patterns first). */}
        <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AdminCatchphraseTrends />
          <AdminTestUserPruner onAfterPrune={loadUsers} />
        </div>

        {/* Catchphrase moderation queue — shown above the user table. */}
        <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AdminCatchphraseQueue />
          <AdminFeedbackList />
        </div>

        <div className="grid lg:grid-cols-[380px_1fr] gap-6">
          {/* LEFT: users list */}
          <section className="border border-white/10 bg-black/40 backdrop-blur-xl" data-testid="admin-users-panel">
            <div className="p-4 border-b border-white/10 flex items-center gap-2">
              <Search className="w-4 h-4 text-white/40" />
              <input
                type="text"
                placeholder="Search email or name…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="admin-search-input"
                className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/30"
              />
            </div>
            <div className="p-3 text-[11px] tracking-[0.25em] uppercase text-white/40 border-b border-white/10">
              {loading ? "Loading…" : `${filteredUsers.length} player${filteredUsers.length !== 1 ? "s" : ""}`}
            </div>
            <div className="max-h-[70vh] overflow-y-auto" data-testid="admin-users-list">
              {filteredUsers.map((u) => (
                <button
                  key={u.user_id}
                  onClick={() => loadDetail(u.user_id)}
                  data-testid={`admin-user-row-${u.user_id}`}
                  className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${
                    selectedId === u.user_id ? "bg-[#FFCC00]/10 border-l-2 border-l-[#FFCC00]" : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {u.picture ? (
                      <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2"
                        style={{ background: u.car_summary.body_color, borderColor: u.car_summary.accent_color, color: u.car_summary.accent_color }}
                      >
                        {u.name?.[0]?.toUpperCase() || "?"}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{u.name}</div>
                      <div className="text-[11px] text-white/50 truncate">{u.email}</div>
                    </div>
                    <div className="text-right text-[11px]">
                      <div className="text-[#FFCC00] font-mono">{u.currency?.toLocaleString()}¢</div>
                      <div className="text-white/50">
                        HP {u.car_summary.hp}/{u.car_summary.max_hp}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
              {!loading && filteredUsers.length === 0 && (
                <div className="p-6 text-center text-white/40 text-sm">No players match.</div>
              )}
            </div>
          </section>

          {/* RIGHT: selected user detail + actions */}
          <section className="min-h-[60vh]">
            {!detail ? (
              <div className="h-full flex items-center justify-center border border-white/10 bg-black/40 p-12 text-white/40" data-testid="admin-empty-detail">
                Select a player on the left to inspect & modify their garage.
              </div>
            ) : (
              <AdminDetail
                detail={detail}
                isSelf={user?.user_id === detail.user.user_id}
                onChanged={async () => {
                  await Promise.all([loadUsers(), loadDetail(detail.user.user_id)]);
                }}
                onDeleted={async () => {
                  // Wipe the right-pane selection and refresh the list.
                  setSelectedId(null);
                  setDetail(null);
                  await loadUsers();
                }}
              />
            )}
          </section>
        </div>
      </div>
    </AppLayout>
  );
}

function AdminDetail({ detail, onChanged, onDeleted, isSelf }) {
  const { user: u, car, templates } = detail;
  const [name, setName] = useState(car.name || "");
  const [body, setBody] = useState(car.body_color || "#FF3B30");
  const [accent, setAccent] = useState(car.accent_color || "#FFCC00");
  const [grantAmount, setGrantAmount] = useState(500);
  // Award-badge form: preset dropdown + free-text custom badge.
  const [badgeChoice, setBadgeChoice] = useState("GOLDEN GLOBETROTTER");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(car.name || "");
    setBody(car.body_color || "#FF3B30");
    setAccent(car.accent_color || "#FFCC00");
  }, [car]);

  const wrap = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const applyCustomize = () => wrap(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/${u.user_id}/customize`,
        { name, body_color: body, accent_color: accent },
        { withCredentials: true });
      toast.success("Vehicle customized");
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Customize failed");
    }
  });

  const selectTemplate = (templateId) => wrap(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/${u.user_id}/select`,
        { template_id: templateId },
        { withCredentials: true });
      toast.success(`Swapped to ${templates[templateId]?.name || templateId}`);
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Template swap failed");
    }
  });

  const repair = () => wrap(async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/users/${u.user_id}/repair`, {},
        { withCredentials: true });
      toast.success(`Repaired +${res.data.hp_restored} HP`);
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Repair failed");
    }
  });

  const grant = () => wrap(async () => {
    const amount = parseInt(grantAmount, 10);
    if (!Number.isFinite(amount) || amount === 0) {
      toast.error("Enter a non-zero amount");
      return;
    }
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/users/${u.user_id}/grant`,
        { amount },
        { withCredentials: true });
      toast.success(`Credits adjusted by ${res.data.delta > 0 ? "+" : ""}${res.data.delta}`);
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed");    }
  });

  const awardBadge = () => wrap(async () => {
    const badge = (badgeChoice || "").trim();
    if (!badge) { toast.error("Pick or type a badge name"); return; }
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/users/${u.user_id}/award-badge`,
        { badge, award_globetrotter_bonus: badge === "GOLDEN GLOBETROTTER" },
        { withCredentials: true });
      if (res.data.already_had) {
        toast(`${u.name} already had ${badge}`, { icon: "ℹ️" });
      } else {
        const bonusText = res.data.bonus > 0 ? ` (+${res.data.bonus} credits)` : "";
        toast.success(`✓ Awarded ${badge} to ${u.name}${bonusText}`);
      }
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Award failed");
    }
  });

  const unlockAllCities = () => wrap(async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/users/${u.user_id}/unlock-cities`,
        {},
        { withCredentials: true });
      const n = (res.data.newly_cleared || []).length;
      if (n === 0) {
        toast(`${u.name} already had all cities unlocked`, { icon: "ℹ️" });
      } else {
        toast.success(`🗺️ Unlocked ${n} ${n === 1 ? "city" : "cities"} for ${u.name}`);
      }
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlock failed");
    }
  });

  const seedFastestClear = () => wrap(async () => {
    const cityId = (window.prompt(
      "Which city ID? (e.g. detroit, paris, tokyo, london, nyc)",
      "detroit",
    ) || "").trim().toLowerCase();
    if (!cityId) return;
    const timeStr = window.prompt("Finish time in seconds (e.g. 16)", "16");
    if (timeStr === null) return;
    const timeSeconds = parseFloat(timeStr);
    if (!Number.isFinite(timeSeconds) || timeSeconds <= 0.5) {
      toast.error("Enter a valid time > 0.5s"); return;
    }
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/users/${u.user_id}/seed-fastest-clear`,
        { city_id: cityId, time_seconds: timeSeconds, items_collected: 5 },
        { withCredentials: true },
      );
      toast.success(`🏁 Seeded ${u.name}: ${res.data.city_name} ${res.data.time_seconds}s`);
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    }
  });

  const deleteUser = () => wrap(async () => {
    // Two-step confirm — destructive + cascades across collections.
    const ok = window.confirm(
      `Delete ${u.name} (${u.email}) PERMANENTLY?\n\n` +
      `This removes the user, their sessions, hosted lobbies, feedback, ` +
      `and queued catchphrases. Cannot be undone.`,
    );
    if (!ok) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${u.user_id}`, {
        withCredentials: true,
      });
      toast.success(`${u.name} deleted`);
      await onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  });

  const toggleSuspend = () => wrap(async () => {
    const willSuspend = !u.is_suspended;
    let reason = null;
    if (willSuspend) {
      reason = window.prompt(
        `Reason for suspending ${u.name}? (optional, shown to the user)`,
        "",
      );
      if (reason === null) return; // cancel
    }
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${u.user_id}/suspend`,
        { suspended: willSuspend, reason: reason || null },
        { withCredentials: true },
      );
      toast.success(willSuspend ? `${u.name} suspended` : `${u.name} reinstated`);
      await onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suspend failed");
    }
  });

  const messageUser = () => wrap(async () => {
    const subject = window.prompt("Subject", "Message from Admin");
    if (subject === null) return;
    const body = window.prompt(`Message to ${u.name}:`, "");
    if (!body) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${u.user_id}/message`,
        { subject, body },
        { withCredentials: true },
      );
      toast.success(`Message sent to ${u.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  });

  return (
    <div className="space-y-5" data-testid="admin-detail">
      {/* Header */}
      <div className="border border-white/10 bg-black/40 p-6">
        <div className="flex items-start gap-4">
          {u.picture ? (
            <img src={u.picture} alt={u.name} className="w-16 h-16 rounded-full object-cover border border-white/10" />
          ) : (
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold border-4"
              style={{ background: car.body_color, borderColor: car.accent_color, color: car.accent_color }}
            >
              {u.name?.[0]?.toUpperCase() || "?"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="label-kicker">Player</div>
            <div className="font-heading text-2xl uppercase tracking-tight truncate" data-testid="admin-detail-name">{u.name}</div>
            <div className="text-white/60 text-sm truncate" data-testid="admin-detail-email">{u.email}</div>
            <div className="mt-2 flex flex-wrap gap-4 text-xs text-white/70">
              <span className="font-mono">{u.user_id}</span>
              <span className="text-[#FFCC00] flex items-center gap-1"><Coins className="w-3 h-3" />{u.currency?.toLocaleString()}</span>
              <span>Comp Wins: {u.competitive_wins ?? 0} · Races: {u.races}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Customize */}
      <div className="border border-white/10 bg-black/40 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Palette className="w-4 h-4 text-[#FFCC00]" />
          <h3 className="font-heading text-xl uppercase tracking-tight">Customize</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] tracking-[0.25em] uppercase text-white/50">Car Name</span>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              data-testid="admin-car-name-input"
              className="mt-1 w-full bg-black/50 border border-white/10 px-3 py-2 text-sm focus:border-[#FFCC00] outline-none"
            />
          </label>
          <div className="flex gap-3">
            <label className="block">
              <span className="text-[10px] tracking-[0.25em] uppercase text-white/50">Body</span>
              <input type="color" value={body} onChange={(e) => setBody(e.target.value)} data-testid="admin-body-color-input" className="mt-1 w-14 h-10 bg-black/50 border border-white/10 cursor-pointer" />
            </label>
            <label className="block">
              <span className="text-[10px] tracking-[0.25em] uppercase text-white/50">Accent</span>
              <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} data-testid="admin-accent-color-input" className="mt-1 w-14 h-10 bg-black/50 border border-white/10 cursor-pointer" />
            </label>
            <div className="ml-auto flex items-end">
              <button onClick={applyCustomize} disabled={busy} data-testid="admin-apply-customize-btn"
                className="px-5 py-2 bg-[#FFCC00] text-black font-heading uppercase text-sm tracking-wider hover:bg-[#FFD93D] disabled:opacity-50">
                Save
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Template swap */}
      <div className="border border-white/10 bg-black/40 p-6">
        <div className="flex items-center gap-2 mb-4">
          <CarIcon className="w-4 h-4 text-[#FFCC00]" />
          <h3 className="font-heading text-xl uppercase tracking-tight">Swap Vehicle</h3>
          <span className="ml-auto text-xs text-white/50">current: <b>{car.kind}</b></span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {templates && Object.entries(templates).map(([id, t]) => (
            <button
              key={id}
              onClick={() => selectTemplate(id)}
              disabled={busy}
              data-testid={`admin-template-${id}-btn`}
              className={`p-3 border text-left transition-colors ${
                car.kind === id ? "border-[#FFCC00] bg-[#FFCC00]/10" : "border-white/10 hover:border-white/30"
              } disabled:opacity-50`}
            >
              <div className="font-heading text-sm uppercase">{t.name}</div>
              <div className="text-[10px] text-white/50 mt-1">HP {t.max_hp} · Spd {t.top_speed}/5</div>
            </button>
          ))}
        </div>
      </div>

      {/* Repair + Grant */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="border border-white/10 bg-black/40 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Wrench className="w-4 h-4 text-[#FFCC00]" />
            <h3 className="font-heading text-xl uppercase tracking-tight">Repair HP</h3>
          </div>
          <p className="text-xs text-white/60 mb-3">Restore this vehicle to full HP — free of charge. Current: <b>{car.hp}/{car.max_hp}</b></p>
          <button onClick={repair} disabled={busy || car.hp >= car.max_hp} data-testid="admin-repair-btn"
            className="w-full px-4 py-2 border border-[#34C759] text-[#34C759] font-heading uppercase text-sm tracking-wider hover:bg-[#34C759]/10 disabled:opacity-40">
            {car.hp >= car.max_hp ? "Already Full" : "Repair to Full"}
          </button>
        </div>

        <div className="border border-white/10 bg-black/40 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Coins className="w-4 h-4 text-[#FFCC00]" />
            <h3 className="font-heading text-xl uppercase tracking-tight">Adjust Credits</h3>
          </div>
          <p className="text-xs text-white/60 mb-3">Grant or deduct credits. Use negative values to deduct.</p>
          <div className="flex gap-2">
            <input
              type="number"
              value={grantAmount}
              onChange={(e) => setGrantAmount(e.target.value)}
              data-testid="admin-grant-amount-input"
              className="flex-1 bg-black/50 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#FFCC00] outline-none"
            />
            <button onClick={grant} disabled={busy} data-testid="admin-grant-btn"
              className="px-5 py-2 bg-[#FFCC00] text-black font-heading uppercase text-sm tracking-wider hover:bg-[#FFD93D] disabled:opacity-50">
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Awards & Cities — manual badge grant + unlock-all-cities */}
      <div className="grid sm:grid-cols-2 gap-4" data-testid="admin-awards-cities">
        <div className="border border-white/10 bg-black/40 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-4 h-4 text-[#FFCC00]" />
            <h3 className="font-heading text-xl uppercase tracking-tight">Award Badge</h3>
          </div>
          <p className="text-xs text-white/60 mb-3">
            Manually grant a badge — useful when a player earned the
            GOLDEN GLOBETROTTER pre-expansion (10-city era). Granting
            GOLDEN GLOBETROTTER also pays the standard +5000 credit bonus.
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={badgeChoice}
              onChange={(e) => setBadgeChoice(e.target.value)}
              data-testid="admin-badge-input"
              list="admin-badge-presets"
              className="flex-1 bg-black/50 border border-white/10 px-3 py-2 text-sm focus:border-[#FFCC00] outline-none"
            />
            <datalist id="admin-badge-presets">
              <option value="GOLDEN GLOBETROTTER" />
              <option value="Wingman" />
              <option value="Local Legend — Tokyo" />
              <option value="Local Legend — Detroit" />
              <option value="Local Legend — Paris" />
              <option value="Champion" />
              <option value="World Champion" />
            </datalist>
            <button
              onClick={awardBadge} disabled={busy || !badgeChoice.trim()} data-testid="admin-award-badge-btn"
              className="px-5 py-2 bg-[#FFCC00] text-black font-heading uppercase text-sm tracking-wider hover:bg-[#FFD93D] disabled:opacity-50">
              Award
            </button>
          </div>
          {(u.badges || []).length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] tracking-[0.25em] uppercase text-white/50 mb-1">Current badges</div>
              <div className="flex flex-wrap gap-1.5">
                {(u.badges || []).map((b) => (
                  <span key={b} className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#FFCC00]/15 border border-[#FFCC00]/40 text-[#FFCC00]">{b}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border border-white/10 bg-black/40 p-6">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-4 h-4 text-[#34C759]" />
            <h3 className="font-heading text-xl uppercase tracking-tight">Unlock All Cities</h3>
          </div>
          <p className="text-xs text-white/60 mb-3">
            Marks every city as cleared so the player can race ANY map
            immediately — bypasses the under-15s unlock progression.
            Currently cleared: <b>{Object.keys(u.city_clears || {}).filter((k) => u.city_clears[k]?.cleared).length}/20</b>
          </p>
          <button
            onClick={unlockAllCities} disabled={busy} data-testid="admin-unlock-cities-btn"
            className="w-full px-4 py-2 border border-[#34C759] text-[#34C759] font-heading uppercase text-sm tracking-wider hover:bg-[#34C759]/10 disabled:opacity-40">
            Unlock All 20 Cities
          </button>
          <button
            onClick={seedFastestClear} disabled={busy} data-testid="admin-seed-clear-btn"
            className="mt-2 w-full px-4 py-2 border border-[#FF9F0A] text-[#FF9F0A] font-heading uppercase text-sm tracking-wider hover:bg-[#FF9F0A]/10 disabled:opacity-40">
            🏁 Seed Fastest Clear
          </button>
        </div>
      </div>

      {/* Moderation actions — message + suspend */}
      <div className="grid sm:grid-cols-2 gap-4" data-testid="admin-moderation">
        <div className="border border-white/10 bg-black/40 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-4 h-4 text-[#00F0FF]" />
            <h3 className="font-heading text-xl uppercase tracking-tight">Message User</h3>
          </div>
          <p className="text-xs text-white/60 mb-3">
            Send a private note. The user will see an inbox badge and can read
            it from the profile menu.
          </p>
          <button
            onClick={messageUser}
            disabled={busy}
            data-testid="admin-message-user-btn"
            className="w-full px-4 py-2 border border-[#00F0FF] text-[#00F0FF] font-heading uppercase text-sm tracking-wider hover:bg-[#00F0FF]/10 disabled:opacity-40"
          >
            <Mail className="w-4 h-4 inline mr-2 -mt-0.5" />
            Compose message
          </button>
        </div>

        <div className={`border p-6 ${u.is_suspended ? "border-[#FF3B30] bg-[#FF3B30]/5" : "border-white/10 bg-black/40"}`}>
          <div className="flex items-center gap-2 mb-3">
            {u.is_suspended ? (
              <Ban className="w-4 h-4 text-[#FF3B30]" />
            ) : (
              <ShieldCheck className="w-4 h-4 text-[#FFCC00]" />
            )}
            <h3 className={`font-heading text-xl uppercase tracking-tight ${u.is_suspended ? "text-[#FF3B30]" : ""}`}>
              {u.is_suspended ? "Suspended" : "Account Status"}
            </h3>
          </div>
          {u.is_suspended ? (
            <>
              <p className="text-xs text-white/70 mb-3">
                <b>Reason:</b> {u.suspended_reason || "(none recorded)"}
                <br />
                <span className="text-white/40">
                  Suspended at {u.suspended_at ? new Date(u.suspended_at).toLocaleString() : "?"}.
                  This user cannot create or join races.
                </span>
              </p>
              <button
                onClick={toggleSuspend}
                disabled={busy || isSelf}
                data-testid="admin-unsuspend-btn"
                className="w-full px-4 py-2 border border-[#34C759] text-[#34C759] font-heading uppercase text-sm tracking-wider hover:bg-[#34C759]/10 disabled:opacity-40"
              >
                <ShieldCheck className="w-4 h-4 inline mr-2 -mt-0.5" />
                Reinstate user
              </button>
            </>
          ) : (
            <>
              <p className="text-xs text-white/60 mb-3">
                Block this user from creating or joining races. Their account
                stays so they can sign in and read your messages explaining why.
              </p>
              <button
                onClick={toggleSuspend}
                disabled={busy || isSelf}
                data-testid="admin-suspend-btn"
                className="w-full px-4 py-2 border border-[#FF3B30] text-[#FF3B30] font-heading uppercase text-sm tracking-wider hover:bg-[#FF3B30]/10 disabled:opacity-40"
              >
                <Ban className="w-4 h-4 inline mr-2 -mt-0.5" />
                {isSelf ? "Can't suspend yourself" : "Suspend account"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* DANGER ZONE — full account deletion */}
      <div className="border-2 border-[#FF3B30]/50 bg-[#FF3B30]/5 p-6" data-testid="admin-danger-zone">
        <div className="flex items-center gap-2 mb-3">
          <Trash2 className="w-4 h-4 text-[#FF3B30]" />
          <h3 className="font-heading text-xl uppercase tracking-tight text-[#FF3B30]">Danger Zone</h3>
        </div>
        <p className="text-xs text-white/60 mb-3">
          Permanently delete this account and every record tied to it. Use this
          to prune leftover test users from the leaderboard. Cannot be undone.
        </p>
        <button
          onClick={deleteUser}
          disabled={busy || isSelf}
          data-testid="admin-delete-user-btn"
          title={isSelf ? "You can't delete your own admin account" : "Delete this user permanently"}
          className={`w-full px-4 py-2 border-2 border-[#FF3B30] font-heading uppercase text-sm tracking-wider transition-colors ${
            isSelf || busy
              ? "text-[#FF3B30]/40 cursor-not-allowed"
              : "text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white"
          }`}
        >
          <Trash2 className="w-4 h-4 inline mr-2 -mt-0.5" />
          {isSelf ? "Can't delete your own admin account" : `Delete ${u.name} forever`}
        </button>
      </div>
    </div>
  );
}
